import React from "react";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import { Avatar, Badge, Box, Button, Flex, Icon, IconButton, Link, SimpleGrid, Text } from "@chakra-ui/react";
import {
    MdBlock, MdCheckCircle, MdEmail, MdContactPhone, MdAssignment, MdAccountBalance,
    MdAccountBalanceWallet, MdCardMembership, MdFactCheck, MdContacts, MdKey, MdLockReset, MdContentCopy, MdOpenInNew,
} from "react-icons/md";
import { ROLE_LABELS, ROLE_COLORS, DEFAULT_ROLE_COLOR } from "../usersListConstants";
import { SectionTitle, KvRow, StatTile, ManageRow, useInspectorTokens } from "./inspectorUi";
import { buildMapsUrl, formatCoordinates, hasCoordinates } from "tools/geo";

const ACTIVATION_TONE = { bg: "rgba(1,181,116,0.10)", border: "rgba(1,181,116,0.35)", color: "#01885A" };
const RESET_TONE = { bg: "rgba(255,152,0,0.12)", border: "rgba(255,152,0,0.35)", color: "#B26A00" };

const DAYS_FR = [["monday", "Lun"], ["tuesday", "Mar"], ["wednesday", "Mer"], ["thursday", "Jeu"],
    ["friday", "Ven"], ["saturday", "Sam"], ["sunday", "Dim"]];

/**
 * Builds the clickable GPS coordinates node for the identity section.
 * @param {Object} profile - User profile (my_profile)
 * @returns {React.ReactElement|null} Maps link node, or null without coordinates
 */
function buildCoordinatesValue(profile) {
    if (!hasCoordinates(profile)) return null;
    return (
        <Link href={buildMapsUrl(profile.latitude, profile.longitude)} isExternal display="inline-flex" alignItems="center" gap="4px" color="brand.500">
            {formatCoordinates(profile.latitude, profile.longitude)}
            <Icon as={MdOpenInNew} w="14px" h="14px" />
        </Link>
    );
}

/**
 * Builds the identity key/value rows from a profile.
 * The GPS row is only included when the profile carries coordinates.
 * @param {Object} profile - User profile (my_profile)
 * @returns {Array<{label:string, value:*}>}
 */
function buildIdentityRows(profile) {
    const p = profile || {};
    return [
        { label: "Nom", value: [p.name, p.lastname].filter(Boolean).join(" ") || "—" },
        { label: "Nom d'artiste", value: p.artist_name },
        { label: "Email", value: p.email },
        { label: "Téléphone", value: p.phone },
        { label: "Genre", value: p.gender },
        { label: "Naissance", value: p.birth ? dayjs(p.birth).format("DD/MM/YYYY") : null },
        { label: "Adresse", value: p.address },
        ...(hasCoordinates(p) ? [{ label: "Coordonnées GPS", value: buildCoordinatesValue(p) }] : []),
        { label: "Pays", value: p.country },
        { label: "Devise", value: p.currency },
    ];
}

/**
 * Formats travel expenses into a short label.
 * @param {Object} condition - Booking conditions
 * @param {string} currency - Currency symbol
 * @returns {string}
 */
function formatTravel(condition, currency) {
    const t = condition?.travel_expenses;
    if (!t?.from) return "Gratuit";
    return t.to ? `${t.from}${currency} – ${t.to}${currency}` : `${t.from}${currency}`;
}

/**
 * Returns a short label listing the available booking days.
 * @param {Object} condition - Booking conditions with day booleans
 * @returns {string}
 */
function availabilityLabel(condition) {
    const open = DAYS_FR.filter(([key]) => condition?.[key]).map(([, fr]) => fr);
    return open.length ? open.join(" · ") : "Aucune";
}

/**
 * Highlighted callout showing a single validation code with one-click copy.
 * @param {Object} props - { icon, label, code, tone }
 * @returns {React.ReactElement}
 */
function CodeCallout({ icon, label, code, tone }) {
    return (
        <Flex align="center" gap="10px" p="9px 12px" mb="8px" borderRadius="10px"
            bg={tone.bg} border="1px solid" borderColor={tone.border}>
            <Icon as={icon} color={tone.color} boxSize="18px" flexShrink={0} />
            <Box flex="1" minW={0}>
                <Text fontSize="0.62rem" fontWeight="700" textTransform="uppercase" letterSpacing="0.4px" color={tone.color}>{label}</Text>
                <Text fontSize="0.85rem" fontWeight="700" fontFamily="monospace" color={tone.color}
                    wordBreak="break-all" sx={{ userSelect: "all" }}>{code}</Text>
            </Box>
            <IconButton aria-label="Copier le code" size="xs" variant="ghost" color={tone.color} flexShrink={0}
                icon={<Icon as={MdContentCopy} />}
                onClick={() => { navigator.clipboard?.writeText(code); toast.success("Code copié"); }} />
        </Flex>
    );
}

/**
 * Prominent validation-codes block (account activation key / password-reset key).
 * Rendered only when the API returns a pending key, so admins never miss them.
 * @param {Object} props - { keyActivate, keyPasswordReset }
 * @returns {React.ReactElement|null}
 */
export function ValidationCodes({ keyActivate, keyPasswordReset }) {
    if (!keyActivate && !keyPasswordReset) return null;
    return (
        <Box mb="16px">
            <SectionTitle>Codes de validation</SectionTitle>
            {keyActivate && <CodeCallout icon={MdKey} label="Activation du compte" code={keyActivate} tone={ACTIVATION_TONE} />}
            {keyPasswordReset && <CodeCallout icon={MdLockReset} label="Réinitialisation du mot de passe" code={keyPasswordReset} tone={RESET_TONE} />}
        </Box>
    );
}

/**
 * Sticky inspector header: avatar, identity, role/ban badges and quick actions.
 * @param {Object} props - { profile, role, banned, onBan, onEmail }
 * @returns {React.ReactElement}
 */
export function UserHeaderBar({ profile, role, banned, onBan, onEmail }) {
    const t = useInspectorTokens();
    const c = ROLE_COLORS[role] || DEFAULT_ROLE_COLOR;
    const name = [profile?.name, profile?.lastname].filter(Boolean).join(" ") || profile?.artist_name || "Utilisateur";
    return (
        <Box p="14px 16px" borderBottom="1px solid" borderColor={t.border}>
            <Flex gap="12px" align="center">
                <Avatar src={profile?.photo || undefined} name={name} w="46px" h="46px" />
                <Box flex="1" minW={0}>
                    <Flex align="center" gap="6px" flexWrap="wrap">
                        <Text fontSize="0.95rem" fontWeight="700" color={t.value} isTruncated>{name}</Text>
                        <Badge bg={c.bg} color={c.color} borderColor={c.border} borderWidth="1px"
                            borderRadius="7px" px="7px" fontSize="0.6rem" textTransform="none">
                            {ROLE_LABELS[role] || role || "—"}
                        </Badge>
                        {banned && <Badge colorScheme="red" borderRadius="7px" px="6px" fontSize="0.6rem">Banni</Badge>}
                    </Flex>
                    <Text fontSize="0.72rem" color={t.label} fontFamily="monospace" isTruncated>{profile?.email}</Text>
                </Box>
            </Flex>
            <Flex gap="8px" mt="10px">
                <Button size="xs" flex="1" borderRadius="8px" colorScheme={banned ? "green" : "red"} variant="outline"
                    leftIcon={<Icon as={banned ? MdCheckCircle : MdBlock} />} onClick={onBan}>
                    {banned ? "Débannir" : "Bannir"}
                </Button>
                <Button size="xs" flex="1" borderRadius="8px" colorScheme="brand" variant="outline"
                    leftIcon={<Icon as={MdEmail} />} onClick={onEmail}>Email</Button>
            </Flex>
        </Box>
    );
}

/**
 * Quick stat tiles (contacts / demands) shown at the top of the body.
 * @param {Object} props - { data }
 * @returns {React.ReactElement}
 */
export function UserStatRow({ data }) {
    return (
        <SimpleGrid columns={2} spacing="8px" mb="16px">
            <StatTile label="Prises de contact" value={(data.artist_contacted || []).length} icon={MdContactPhone} />
            <StatTile label="Demandes" value={(data.auditor_contacts || []).length} icon={MdContacts} />
        </SimpleGrid>
    );
}

/**
 * Identity section: profile key/value rows + account status.
 * @param {Object} props - { profile, banned }
 * @returns {React.ReactElement}
 */
export function IdentitySection({ profile, banned }) {
    return (
        <Box mb="18px">
            <SectionTitle>Identité</SectionTitle>
            {buildIdentityRows(profile).map((r) => <KvRow key={r.label} label={r.label} value={r.value} />)}
            <KvRow label="Statut du compte" value={banned ? "Banni" : "Actif"}
                valueColor={banned ? "red.500" : "green.500"} />
        </Box>
    );
}

/**
 * Booking conditions section (artists only).
 * @param {Object} props - { condition, currency }
 * @returns {React.ReactElement|null}
 */
export function ConditionsSection({ condition, currency }) {
    if (!condition) return null;
    return (
        <Box mb="18px">
            <SectionTitle>Conditions de booking</SectionTitle>
            <KvRow label="Politique de remboursement" value={condition.refund_policy} />
            <KvRow label="Frais de transport" value={formatTravel(condition, currency || "")} />
            <KvRow label="Disponibilité" value={availabilityLabel(condition)} />
        </Box>
    );
}

/**
 * Summarizes the wallet (first wallet balance + frozen flag).
 * @param {Array} wallets - Wallet array from the payload
 * @returns {string}
 */
function walletSummary(wallets) {
    const w = (wallets || [])[0];
    if (!w) return "Aucun portefeuille";
    return `${w.balance ?? 0} ${w.currency || ""}${w.frozen ? " · gelé" : ""}`.trim();
}

/**
 * Builds the manageable-section rows (each opens its full component in a drawer).
 * @param {Object} data - Full user payload
 * @param {boolean} isAuditor - Whether the user is an auditor (fewer sections)
 * @returns {Array<{key:string, icon:Function, label:string, value:string}>}
 */
function buildManageItems(data, isAuditor) {
    const items = [{ key: "contacts", icon: MdContactPhone, label: "Prises de contact", value: `${(data.artist_contacted || []).length} contact(s)` }];
    if (isAuditor) return items;
    const bank = data.banking && Object.keys(data.banking).length ? "Compte renseigné" : "Aucun compte";
    items.push({ key: "demands", icon: MdAssignment, label: "Demandes", value: `${(data.auditor_contacts || []).length} demande(s)` });
    items.push({ key: "bank", icon: MdAccountBalance, label: "Banque", value: bank });
    items.push({ key: "wallet", icon: MdAccountBalanceWallet, label: "Portefeuille", value: walletSummary(data.wallet) });
    items.push({ key: "subscriptions", icon: MdCardMembership, label: "Abonnements", value: "Voir les abonnements" });
    items.push({ key: "service", icon: MdFactCheck, label: "Fiche KantoBiz", value: data.user_services?.[0]?.title || "Voir la fiche" });
    return items;
}

/**
 * List of drill-down rows opening each domain in a management drawer.
 * @param {Object} props - { data, isAuditor, onOpen }
 * @returns {React.ReactElement}
 */
export function ManageList({ data, isAuditor, onOpen }) {
    return (
        <Box mb="6px">
            <SectionTitle>Gestion</SectionTitle>
            {buildManageItems(data, isAuditor).map((item) => (
                <ManageRow key={item.key} icon={item.icon} label={item.label} value={item.value}
                    actionLabel="Ouvrir" onAction={() => onOpen(item.key)} />
            ))}
        </Box>
    );
}
